/**
 * One line in the upload progress list: file name, what is happening to it right
 * now, and a bar for the byte transfer.
 *
 * Deliberately dumb - it renders a `UploadTaskStatus` and nothing else, so it can
 * be reused by any surface that drives the shared upload queue.
 */

import {
  ArrowClockwise,
  CheckCircle,
  Prohibit,
  WarningCircle,
} from "@phosphor-icons/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { formatBytes } from "shared/utils/uploadFormat";
import type {
  UploadTaskState,
  UploadTaskStatus,
} from "shared/hooks/uploadQueue.types";

interface UploadFileProgressRowProps {
  task: UploadTaskStatus;
  /** Omit to hide the retry affordance (e.g. while the batch is still running). */
  onRetry?: (task: UploadTaskStatus) => void;
}

const STATE_LABELS: Record<UploadTaskState, string> = {
  pending: "In wachtrij",
  compressing: "Comprimeren",
  uploading: "Uploaden",
  processing: "Verwerken op server",
  success: "Geüpload",
  error: "Mislukt",
  canceled: "Geannuleerd",
};

const STATE_BADGE_VARIANT: Record<
  UploadTaskState,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  compressing: "secondary",
  uploading: "secondary",
  processing: "secondary",
  success: "default",
  error: "destructive",
  canceled: "outline",
};

/**
 * Icons carry meaning here, so they need real contrast against the white panel -
 * the muted foreground token is the lightest we go.
 */
const StateIcon = ({ state }: { state: UploadTaskState }) => {
  switch (state) {
    case "success":
      return (
        <CheckCircle
          size={18}
          weight="fill"
          className="text-moss-primary shrink-0"
          aria-hidden
        />
      );
    case "error":
      return (
        <WarningCircle
          size={18}
          weight="fill"
          className="text-destructive shrink-0"
          aria-hidden
        />
      );
    case "canceled":
      return (
        <Prohibit
          size={18}
          className="text-muted-foreground shrink-0"
          aria-hidden
        />
      );
    default:
      return (
        <div
          className="border-foreground/25 border-t-foreground h-[18px] w-[18px] shrink-0 animate-spin rounded-full border-2"
          aria-hidden
        />
      );
  }
};

export const UploadFileProgressRow = ({
  task,
  onRetry,
}: UploadFileProgressRowProps) => {
  // A bar is shown for every state where something is actively happening to the
  // file, so a row never sits there looking stalled.
  const showBar =
    task.state === "compressing" ||
    task.state === "uploading" ||
    task.state === "processing";
  const percent = Math.round(task.fraction * 100);

  return (
    <li className="border-border flex flex-col gap-2 border-b py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          <StateIcon state={task.state} />
        </div>

        <div className="min-w-0 flex-1">
          <p
            className="text-foreground truncate text-sm font-medium"
            title={task.displayName}
          >
            {task.displayName}
          </p>

          <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span>{formatBytes(task.effectiveSize)}</span>

            {task.compressionNote && (
              <span className="text-moss-primary">
                {task.compressionNote} (was {formatBytes(task.originalSize)})
              </span>
            )}

            {task.state === "uploading" && task.bytesTotal > 0 && (
              <span className="text-foreground font-medium tabular-nums">
                {percent}% · {formatBytes(task.bytesSent)} van{" "}
                {formatBytes(task.bytesTotal)}
              </span>
            )}

            {task.state === "processing" && (
              <span>Opslaan in SharePoint...</span>
            )}

            {task.attempts > 1 && task.state !== "success" && (
              <span>poging {task.attempts}</span>
            )}
          </div>

          {task.errorMessage && task.state === "error" && (
            <p className="text-destructive mt-1 text-xs">{task.errorMessage}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={STATE_BADGE_VARIANT[task.state]}>
            {STATE_LABELS[task.state]}
          </Badge>

          {onRetry && task.state === "error" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRetry(task)}
              aria-label={`Opnieuw proberen: ${task.displayName}`}
            >
              <ArrowClockwise size={14} aria-hidden />
              Opnieuw
            </Button>
          )}
        </div>
      </div>

      {showBar && (
        <div className="pl-[30px]">
          <Progress
            value={percent}
            aria-label={`Uploadvoortgang ${task.displayName}`}
          />
        </div>
      )}
    </li>
  );
};

export default UploadFileProgressRow;
